// src/hooks/useOverviewData.js
import {useCallback, useEffect, useMemo, useState} from "react";
import {fetchJson} from "../services/apiClient";

function isValidYmd(dateStr) {
  // Validate YYYY-MM-DD without accepting invalid dates like 2025-13-40.
  if (typeof dateStr !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}` === dateStr;
}

function buildDefaultHours() {
  // Return 24 rows 00..23 with zeros.
  const out = [];
  for (let h = 0; h < 24; h++) {
    out.push({hour: String(h).padStart(2, "0"), count: 0, percent: 0});
  }
  return out;
}

function buildDefaultSegmentsByHour() {
  // Return {"00":[{width:100,type:"off"}],...,"23":[...]}.
  const out = {};
  for (let h = 0; h < 24; h++) {
    out[String(h).padStart(2, "0")] = [{width: 100, type: "off"}];
  }
  return out;
}

export function useOverviewData(selectedDate) {
  const [overviewMachines, setOverviewMachines] = useState([]);
  const [segmentsMachines, setSegmentsMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOverview = useCallback(async () => {
    if (!isValidYmd(selectedDate)) return;
    const data = await fetchJson(`/api/ui/overview?date=${selectedDate}`);
    setOverviewMachines(Array.isArray(data?.machines) ? data.machines : []);
  }, [selectedDate]);

  const fetchSegments = useCallback(async () => {
    if (!isValidYmd(selectedDate)) return;
    const data = await fetchJson(`/api/ui/segments?date=${selectedDate}`);
    setSegmentsMachines(Array.isArray(data?.machines) ? data.machines : []);
  }, [selectedDate]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        await Promise.all([fetchOverview(), fetchSegments()]);
      } catch (err) {
        console.error("Error fetching overview data:", err);
        if (mounted) {
          setOverviewMachines([]);
          setSegmentsMachines([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [fetchOverview, fetchSegments]);

  useEffect(() => {
    // Refresh overview periodically (latest status + aggregates may change).
    const intervalId = setInterval(() => {
      fetchOverview().catch((err) => console.error("Error refreshing overview:", err));
    }, 60000);

    return () => clearInterval(intervalId);
  }, [fetchOverview]);

  const segmentsBySerial = useMemo(() => {
    const map = {};
    for (const m of segmentsMachines) {
      const serialNumber = m?.serialNumber;
      if (!serialNumber) continue;

      const byHour = buildDefaultSegmentsByHour();
      const hoursArr = Array.isArray(m?.hours) ? m.hours : [];

      for (const h of hoursArr) {
        const hh = String(h?.hour ?? "").padStart(2, "0");
        if (!/^\d{2}$/.test(hh)) continue;

        const segs = Array.isArray(h?.segments) && h.segments.length > 0 ? h.segments : [{width: 100, type: "off"}];

        byHour[hh] = segs;
      }

      map[serialNumber] = byHour;
    }
    return map;
  }, [segmentsMachines]);

  const machines = useMemo(() => {
    return overviewMachines.map((m) => {
      const serialNumber = m.serialNumber;
      return {
        // Keep both naming styles to avoid breaking legacy components.
        serialNumber,
        serial_number: serialNumber,

        name: m.name,
        type: m.type,
        country: m.country,

        job: m.latestJob ?? "-",
        status: m.latestStatus ?? "Inactive",

        shiftModel: m.shiftModel ?? "-",

        hours: Array.isArray(m.hours) && m.hours.length === 24 ? m.hours : buildDefaultHours(),
        segmentsByHour: segmentsBySerial[serialNumber] || buildDefaultSegmentsByHour(),
      };
    });
  }, [overviewMachines, segmentsBySerial]);

  return {
    machines,
    loading,
    refreshOverview: fetchOverview,
    refreshSegments: fetchSegments,
  };
}
